import { z } from "zod";

/**
 * Contracts HỤI / HỌ (ROSCA) — US-E20.F1-0207-S02 (@UC9321). 🔴 Tiền = `string` BigInt minor
 * (KHÔNG number/float lộ ra JSON — B1). Mọi schema `.strict()` (reject field dư).
 *
 * Mirror `loanResponseSchema` (debts.ts) — hụi tái dùng khái niệm "hốt = vay từ dây".
 */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

export const HUI_KIND = {
  DEAD: "dead", // thứ tự hốt cố định (bốc thăm/định trước)
  LIVE: "live", // đấu giá bỏ lãi mỗi kỳ
} as const;
export type HuiKindContract = (typeof HUI_KIND)[keyof typeof HUI_KIND];

export const HUI_LINE_STATUS_VALUES = ["open", "running", "completed", "broken"] as const;
export const HUI_ROUND_STATUS_VALUES = ["pending", "taken", "settled"] as const;

export const huiKindSchema = z.enum([HUI_KIND.DEAD, HUI_KIND.LIVE]);
export const huiLineStatusSchema = z.enum(HUI_LINE_STATUS_VALUES);
export const huiRoundStatusSchema = z.enum(HUI_ROUND_STATUS_VALUES);

/* ─────────────────────────────── INPUT (validate biên) ─────────────────────────────── */

export const createHuiLineSchema = z
  .object({
    name: z.string().min(1).max(200),
    kind: huiKindSchema,
    // 🔴 faceValueMinor > 0 (chuỗi số nguyên, không cho "0" / âm / thập phân).
    // (refine chạy KỂ CẢ khi regex fail ⇒ tự kiểm pattern trước BigInt() để không ném SyntaxError).
    faceValueMinor: z
      .string()
      .regex(MONEY_INTEGER_STRING_PATTERN)
      .refine(
        (v) => MONEY_INTEGER_STRING_PATTERN.test(v) && BigInt(v) > 0n,
        "faceValueMinor phải > 0",
      ),
    commissionMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).optional(),
    frequency: z.enum(["weekly", "monthly", "quarterly"]).optional(),
    ownerMemberId: z.string().uuid(),
    // 🔴 dây hụi cần ≥ 2 chân (roundCount = số chân = số kỳ).
    memberIds: z.array(z.string().uuid()).min(2),
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict();
export type CreateHuiLineRequest = z.infer<typeof createHuiLineSchema>;

export const placeBidSchema = z
  .object({
    bidderMemberId: z.string().uuid(),
    bidMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();
export type PlaceBidRequest = z.infer<typeof placeBidSchema>;

export const takeRoundSchema = z
  .object({
    /** Hụi chết: bắt buộc đúng người tới lượt. Hụi sống: vắng ⇒ lấy bidder thắng thầu. */
    takerMemberId: z.string().uuid().optional(),
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict();
export type TakeRoundRequest = z.infer<typeof takeRoundSchema>;

export const contributeRoundSchema = z
  .object({
    memberId: z.string().uuid(),
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict();
export type ContributeRoundRequest = z.infer<typeof contributeRoundSchema>;

export const breakHuiSchema = z
  .object({
    reason: z.string().min(1).max(500),
  })
  .strict();
export type BreakHuiRequest = z.infer<typeof breakHuiSchema>;

/* ─────────────────────────────── RESPONSE ─────────────────────────────── */

export const huiLineResponseSchema = z
  .object({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    fundId: z.string().uuid(),
    name: z.string(),
    faceValueMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    commissionMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    roundCount: z.number().int().min(2),
    frequency: z.string(),
    kind: huiKindSchema,
    ownerMemberId: z.string().uuid(),
    status: huiLineStatusSchema,
    brokenReason: z.string().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type HuiLineResponse = z.infer<typeof huiLineResponseSchema>;

export const huiRoundResponseSchema = z
  .object({
    id: z.string().uuid(),
    huiLineId: z.string().uuid(),
    roundNo: z.number().int().min(1),
    takerMemberId: z.string().uuid().nullable(),
    winningBidMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Σ suất đóng của kỳ. */
    grossMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Người hốt THỰC NHẬN = gross − hoa hồng. */
    payoutMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    commissionMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    takeLedgerTxId: z.string().uuid().nullable(),
    commissionLedgerTxId: z.string().uuid().nullable(),
    status: huiRoundStatusSchema,
  })
  .strict();
export type HuiRoundResponse = z.infer<typeof huiRoundResponseSchema>;

export const huiBidResponseSchema = z
  .object({
    id: z.string().uuid(),
    huiRoundId: z.string().uuid(),
    bidderMemberId: z.string().uuid(),
    bidMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    createdAt: z.string().datetime(),
  })
  .strict();
export type HuiBidResponse = z.infer<typeof huiBidResponseSchema>;

/** Vị thế 1 chân hụi. `outstandingMinor` là chuỗi số nguyên CÓ DẤU (âm = pool nợ member). */
export const huiMemberPositionResponseSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    hasTaken: z.boolean(),
    outstandingMinor: z.string().regex(/^-?\d+$/),
    takeOrder: z.number().int().nullable(),
  })
  .strict();
export type HuiMemberPositionResponse = z.infer<typeof huiMemberPositionResponseSchema>;

/** Kết quả đóng kỳ (1 thành viên). */
export const huiContributionResponseSchema = z
  .object({
    huiLineId: z.string().uuid(),
    roundNo: z.number().int().min(1),
    memberId: z.string().uuid(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    ledgerTxId: z.string().uuid(),
    outstandingMinor: z.string().regex(/^-?\d+$/),
  })
  .strict();
export type HuiContributionResponse = z.infer<typeof huiContributionResponseSchema>;

/** Thiệt hại vỡ hụi chia cho 1 nạn nhân (peer-debt append-only). */
export const huiLossShareResponseSchema = z
  .object({
    creditorMemberId: z.string().uuid(),
    debtorMemberId: z.string().uuid(),
    shareMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    debtEntryId: z.string().uuid(),
  })
  .strict();
export type HuiLossShareResponse = z.infer<typeof huiLossShareResponseSchema>;

export const breakHuiResponseSchema = z
  .object({
    huiLineId: z.string().uuid(),
    status: z.literal("broken"),
    /** Σ nợ tồn của (các) con nợ — CHỐT từ ledger, KHÔNG xoá (P4.3). */
    totalOutstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    defaulterMemberIds: z.array(z.string().uuid()),
    lossShares: z.array(huiLossShareResponseSchema),
    /** Người gánh ĐỒNG LẺ — CÓ TÊN (P1.3). Null khi không có thiệt hại để chia. */
    residualHolderId: z.string().uuid().nullable(),
  })
  .strict();
export type BreakHuiResponse = z.infer<typeof breakHuiResponseSchema>;

export const huiLineDetailResponseSchema = z
  .object({
    line: huiLineResponseSchema,
    rounds: z.array(huiRoundResponseSchema),
    members: z.array(huiMemberPositionResponseSchema),
    /** Σ tiền mặt còn trong sổ hụi (pool) — 0 khi mọi kỳ đã tất toán và hoa hồng đã chi. */
    poolCashMinor: z.string().regex(/^-?\d+$/),
  })
  .strict();
export type HuiLineDetailResponse = z.infer<typeof huiLineDetailResponseSchema>;

/* ─────────────────────────────── Mã lỗi ─────────────────────────────── */

export const HUI_NOT_YOUR_TURN_CODE = "HUI_NOT_YOUR_TURN";
export const HUI_ALREADY_TAKEN_CODE = "HUI_ALREADY_TAKEN";
export const HUI_BID_AFTER_TAKEN_CODE = "HUI_BID_AFTER_TAKEN";
export const HUI_ROUND_NOT_TAKEN_CODE = "HUI_ROUND_NOT_TAKEN";
export const HUI_LINE_BROKEN_CODE = "HUI_LINE_BROKEN";
export const HUI_COMMISSION_EXCEEDS_CODE = "HUI_COMMISSION_EXCEEDS_PAYOUT";
/**
 * CONFLICT-CODE-ROUND-3 (409, ratchet `conflict-code.invariants`):
 *  · `HUI_LINE_COMPLETED` — dây đã HOÀN TẤT (mọi kỳ đã hốt) ⇒ không ghi thêm giao dịch (cặp với `HUI_LINE_BROKEN`). @UC9322
 *  · `HUI_BID_ALREADY_PLACED` — người này đã bỏ lãi cho kỳ này (unique(huiRoundId, bidderMemberId)). Không `params`. @UC9323
 * Đóng kỳ trùng / ghi trùng khoá ⇒ `LEDGER_CONCURRENT_WRITE` (ledger-write.ts) — KHÔNG mã riêng vùng hụi.
 */
export const HUI_LINE_COMPLETED_CODE = "HUI_LINE_COMPLETED" as const;
export const HUI_BID_ALREADY_PLACED_CODE = "HUI_BID_ALREADY_PLACED" as const;
